/**
 * Announcement Slider Component
 * Handles slider functionality, countdown timers, and accessibility features
 */

class AnnouncementSlider {
  constructor(element) {
    this.slider = element;
    this.slidesContainer = this.slider.querySelector('[data-slides-container]');
    this.slides = Array.from(this.slider.querySelectorAll('[data-slide]'));
    this.indicators = Array.from(this.slider.querySelectorAll('[data-slide-indicator]'));
    this.prevButton = this.slider.querySelector('[data-nav-prev]');
    this.nextButton = this.slider.querySelector('[data-nav-next]');
    this.playPauseButton = this.slider.querySelector('[data-play-pause]');
    
    this.currentSlide = 0;
    this.isPlaying = this.slider.dataset.autoPlay === 'true';
    // Treat sliderSpeed data attribute as seconds; default to 5 seconds
    this.slideSpeed = (parseInt(this.slider.dataset.sliderSpeed, 10) || 5) * 1000;
    this.autoPlayTimer = null;
    this.countdownTimers = new Map();
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    this.init();
  }

  init() {
    if (this.slides.length === 0) return;
    
    this.setupSlides();
    this.setupEventListeners();
    this.setupCountdownTimers();
    this.setupAccessibility();
    
    // Show first slide
    this.showSlide(0);
    
    // Start autoplay if enabled and not reduced motion
    if (this.isPlaying && !this.isReducedMotion) {
      this.startAutoPlay();
    }
    
    // Update play/pause button state
    this.updatePlayPauseButton();
  }

  setupSlides() {
    // Hide all slides initially
    this.slides.forEach((slide, index) => {
      slide.style.display = index === 0 ? 'block' : 'none';
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    });
  }

  setupEventListeners() {
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.previousSlide());
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }
    
    // Play/pause button
    if (this.playPauseButton) {
      this.playPauseButton.addEventListener('click', () => this.togglePlayPause());
    }
    
    // Slide indicators
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Keyboard navigation
    this.slider.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    // Pause on hover (accessibility)
    this.slider.addEventListener('mouseenter', () => this.pauseAutoPlay());
    this.slider.addEventListener('mouseleave', () => {
      if (this.isPlaying && !this.isReducedMotion) {
        this.startAutoPlay();
      }
    });
    
    // Pause on focus (accessibility)
    this.slider.addEventListener('focusin', () => this.pauseAutoPlay());
    this.slider.addEventListener('focusout', () => {
      if (this.isPlaying && !this.isReducedMotion) {
        this.startAutoPlay();
      }
    });
    
    // Handle reduced motion preference changes
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    mediaQuery.addEventListener('change', (e) => {
      this.isReducedMotion = e.matches;
      if (this.isReducedMotion) {
        this.pauseAutoPlay();
      } else if (this.isPlaying) {
        this.startAutoPlay();
      }
    });
  }

  setupCountdownTimers() {
    this.slides.forEach((slide) => {
      const countdownEnd = slide.dataset.countdownEnd;
      const autoHide = slide.dataset.autoHide === 'true';
      
      if (countdownEnd) {
        const endDate = new Date(countdownEnd);
        if (!isNaN(endDate.getTime())) {
          this.startCountdown(slide, endDate, autoHide);
        }
      }
    });
  }

  setupAccessibility() {
    // Set up ARIA live region for announcements
    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.className = 'sr-only';
    liveRegion.id = 'announcement-live-region';
    document.body.appendChild(liveRegion);
    
    this.liveRegion = liveRegion;
  }

  startCountdown(slide, endDate, autoHide) {
    const timer = slide.querySelector('[data-countdown-timer]');
    if (!timer) return;
    
    const daysEl = timer.querySelector('[data-days]');
    const hoursEl = timer.querySelector('[data-hours]');
    const minutesEl = timer.querySelector('[data-minutes]');
    const secondsEl = timer.querySelector('[data-seconds]');
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = endDate.getTime() - now;
      
      if (distance < 0) {
        // Countdown expired
        if (daysEl) daysEl.textContent = '00';
        if (hoursEl) hoursEl.textContent = '00';
        if (minutesEl) minutesEl.textContent = '00';
        if (secondsEl) secondsEl.textContent = '00';
        
        // Hide slide if auto-hide is enabled
        if (autoHide) {
          this.hideExpiredSlide(slide);
        }
        
        // Clear the interval
        if (this.countdownTimers.has(slide)) {
          clearInterval(this.countdownTimers.get(slide));
          this.countdownTimers.delete(slide);
        }
        
        return;
      }
      
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);
      
      if (daysEl) daysEl.textContent = days.toString().padStart(2, '0');
      if (hoursEl) hoursEl.textContent = hours.toString().padStart(2, '0');
      if (minutesEl) minutesEl.textContent = minutes.toString().padStart(2, '0');
      if (secondsEl) secondsEl.textContent = seconds.toString().padStart(2, '0');
    };
    
    // Update immediately
    updateCountdown();
    
    // Set up interval
    const intervalId = setInterval(updateCountdown, 1000);
    this.countdownTimers.set(slide, intervalId);
  }

  hideExpiredSlide(expiredSlide) {
    const slideIndex = this.slides.indexOf(expiredSlide);
    if (slideIndex === -1) return;
    
    // Remove from slides array
    this.slides.splice(slideIndex, 1);
    
    // Hide the slide
    expiredSlide.style.display = 'none';
    expiredSlide.setAttribute('aria-hidden', 'true');
    
    // Remove corresponding indicator
    if (this.indicators[slideIndex]) {
      this.indicators[slideIndex].remove();
      this.indicators.splice(slideIndex, 1);
    }
    
    // Update slide indices
    this.slides.forEach((slide, index) => {
      slide.dataset.slide = index;
    });
    
    this.indicators.forEach((indicator, index) => {
      indicator.dataset.slideIndicator = index;
    });
    
    // Adjust current slide if necessary
    if (slideIndex <= this.currentSlide) {
      this.currentSlide = Math.max(0, this.currentSlide - 1);
    }
    
    // If no slides left, hide the entire slider
    if (this.slides.length === 0) {
      this.slider.style.display = 'none';
      return;
    }
    
    // If current slide was removed, show the next available slide
    if (this.currentSlide >= this.slides.length) {
      this.currentSlide = 0;
    }
    
    this.showSlide(this.currentSlide);
    
    // Hide controls if only one slide remains
    if (this.slides.length === 1) {
      const controls = this.slider.querySelector('.an-Controls');
      if (controls) {
        controls.style.display = 'none';
      }
    }
  }

  showSlide(index) {
    if (index < 0 || index >= this.slides.length) return;
    
    // Hide current slide
    if (this.slides[this.currentSlide]) {
      this.slides[this.currentSlide].style.display = 'none';
      this.slides[this.currentSlide].setAttribute('aria-hidden', 'true');
    }
    
    // Update current slide index
    this.currentSlide = index;
    
    // Show new slide
    this.slides[this.currentSlide].style.display = 'block';
    this.slides[this.currentSlide].setAttribute('aria-hidden', 'false');
    
    // Update indicators
    this.indicators.forEach((indicator, i) => {
      indicator.setAttribute('aria-selected', i === index ? 'true' : 'false');
      indicator.classList.toggle('active', i === index);
    });
    
    // Announce slide change to screen readers
    this.announceSlideChange();
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  previousSlide() {
    const prevIndex = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  goToSlide(index) {
    if (index === this.currentSlide) return;
    
    this.showSlide(index);
    
    // Restart autoplay timer
    if (this.isPlaying && !this.isReducedMotion) {
      this.restartAutoPlay();
    }
  }

  startAutoPlay() {
    if (this.slides.length <= 1 || this.isReducedMotion) return;
    
    this.autoPlayTimer = setInterval(() => {
      this.nextSlide();
    }, this.slideSpeed);
  }

  pauseAutoPlay() {
    if (this.autoPlayTimer) {
      clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  restartAutoPlay() {
    this.pauseAutoPlay();
    if (this.isPlaying && !this.isReducedMotion) {
      this.startAutoPlay();
    }
  }

  togglePlayPause() {
    this.isPlaying = !this.isPlaying;
    
    if (this.isPlaying && !this.isReducedMotion) {
      this.startAutoPlay();
    } else {
      this.pauseAutoPlay();
    }
    
    this.updatePlayPauseButton();
  }

  updatePlayPauseButton() {
    if (!this.playPauseButton) return;
    
    const playIcon = this.playPauseButton.querySelector('.an-PlayIcon');
    const pauseIcon = this.playPauseButton.querySelector('.an-PauseIcon');
    
    if (this.isPlaying) {
      this.playPauseButton.setAttribute('aria-label', 'Pause slideshow');
      if (playIcon) playIcon.style.display = 'none';
      if (pauseIcon) pauseIcon.style.display = 'block';
    } else {
      this.playPauseButton.setAttribute('aria-label', 'Play slideshow');
      if (playIcon) playIcon.style.display = 'block';
      if (pauseIcon) pauseIcon.style.display = 'none';
    }
  }

  handleKeydown(event) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previousSlide();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextSlide();
        break;
      case ' ':
        event.preventDefault();
        this.togglePlayPause();
        break;
      case 'Home':
        event.preventDefault();
        this.goToSlide(0);
        break;
      case 'End':
        event.preventDefault();
        this.goToSlide(this.slides.length - 1);
        break;
    }
  }

  announceSlideChange() {
    if (!this.liveRegion) return;
    
    const currentSlide = this.slides[this.currentSlide];
    if (!currentSlide) return;
    
    const slideNumber = this.currentSlide + 1;
    const totalSlides = this.slides.length;
    const announcement = `Showing announcement ${slideNumber} of ${totalSlides}`;
    
    this.liveRegion.textContent = announcement;
  }

  destroy() {
    // Clear all timers
    this.pauseAutoPlay();
    this.countdownTimers.forEach((timer) => clearInterval(timer));
    this.countdownTimers.clear();
    
    // Remove live region
    if (this.liveRegion && this.liveRegion.parentNode) {
      this.liveRegion.parentNode.removeChild(this.liveRegion);
    }
  }
}

// Initialize announcement sliders when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('[data-announcement-slider]');
  sliders.forEach(slider => new AnnouncementSlider(slider));
});

// Handle theme editor updates (Shopify specific)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector('[data-announcement-slider]');
    if (slider) {
      new AnnouncementSlider(slider);
    }
  });
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AnnouncementSlider;
}
